/**
 * Sysadmin System Library — Sources list (PR-J).
 *
 * System-scoped library connectors. v1: list / create / edit / delete /
 * publish / unpublish. Wave 4 brings sync schedule + run history under
 * the same mental model as the tenant Sources page.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Loader2, Edit, Trash2, MoreHorizontal, Cloud } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  useSysLibSources, useCreateSysLibSource, useUpdateSysLibSource, useDeleteSysLibSource,
  usePublishSysLibSource, useUnpublishSysLibSource, useBulkPublishSysLib,
  type LifecycleStatus, type SysLibSource,
} from '@/lib/use-sysadmin-library';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  LibraryStatusPill, LibraryUnpublishDialog,
} from '@/components/sysadmin/library-lifecycle';

export const Route = createFileRoute('/sysadmin/library/sources')({
  component: SysLibSourcesPage,
});

const CONNECTOR_KINDS = ['onedrive', 'sharepoint', 'github', 'webhook'] as const;
const SCHEDULES = ['every_5min', 'every_15min', 'every_1h', 'every_6h', 'daily', 'manual_only', 'webhook_only'] as const;

function SysLibSourcesPage() {
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus | 'all'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SysLibSource | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<SysLibSource | null>(null);

  const q = useSysLibSources(statusFilter === 'all' ? undefined : statusFilter);
  // Bug-5 (2026-05-10): envelope flipped `data` → `items` in lockstep with server.
  const items = q.data?.items ?? [];

  const publishMut = usePublishSysLibSource();
  const unpublishMut = useUnpublishSysLibSource();
  const deleteMut = useDeleteSysLibSource();
  const bulkMut = useBulkPublishSysLib();

  const allSelected = items.length > 0 && items.every((s) => selectedIds.has(s.id));

  if (q.isLoading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setShowCreate(true)} size="sm" className="gap-1.5"><Plus className="h-3.5 w-3.5" />New Source</Button>
      </div>

      {selectedIds.size > 0 && (
        <div className="rounded-lg border bg-primary/5 border-primary/30 px-3 py-2 flex items-center gap-3">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button size="sm" disabled={bulkMut.isPending} onClick={() =>
            bulkMut.mutate({ entity: 'source', ids: Array.from(selectedIds) }, {
              onSuccess: (r) => { toast.success(`Published ${r.published} sources`); setSelectedIds(new Set()); },
              onError: (e: any) => toast.error(getApiErrorMessage(e, 'Bulk publish failed')),
            })}>Publish all</Button>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-3 py-2.5 text-left">
                <Checkbox checked={allSelected} aria-label="Select all" onCheckedChange={() => {
                  setSelectedIds((p) => {
                    const n = new Set(p);
                    if (allSelected) for (const s of items) n.delete(s.id);
                    else for (const s of items) n.add(s.id);
                    return n;
                  });
                }} />
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Name</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Kind</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Schedule</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Sync</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Status</th>
              <th className="w-10 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {items.length === 0 ? (
              <tr><td colSpan={7} className="p-12 text-center text-muted-foreground">No system sources.</td></tr>
            ) : items.map((s) => (
              <tr key={s.id} className="hover:bg-muted/30">
                <td className="px-3 py-2.5">
                  <Checkbox aria-label={`Select ${s.displayName}`} checked={selectedIds.has(s.id)} onCheckedChange={() => {
                    setSelectedIds((p) => { const n = new Set(p); n.has(s.id) ? n.delete(s.id) : n.add(s.id); return n; });
                  }} />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Cloud className="h-3.5 w-3.5 text-muted-foreground" />
                    <button className="text-left hover:underline font-medium" onClick={() => setEditing(s)}>{s.displayName}</button>
                  </div>
                </td>
                <td className="px-3 py-2.5"><Badge variant="outline" className="text-[10px]">{s.connectorKind}</Badge></td>
                <td className="px-3 py-2.5 text-xs text-muted-foreground">{s.syncSchedule}</td>
                <td className="px-3 py-2.5">
                  <Badge variant="outline" className={`text-[10px] ${s.syncEnabled ? 'bg-emerald-500/10 text-emerald-600' : 'bg-muted'}`}>
                    {s.syncEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </td>
                <td className="px-3 py-2.5"><LibraryStatusPill status={s.status} /></td>
                <td className="px-3 py-2.5">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Source actions" title="More actions"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditing(s)} className="text-xs"><Edit className="h-3.5 w-3.5 mr-2" />Edit</DropdownMenuItem>
                      {s.status === 'draft' && <DropdownMenuItem onClick={() => publishMut.mutate({ id: s.id }, { onSuccess: () => toast.success('Published') })} className="text-xs">Publish</DropdownMenuItem>}
                      {s.status === 'published' && <DropdownMenuItem onClick={() => setUnpublishTarget(s)} className="text-xs">Unpublish…</DropdownMenuItem>}
                      {s.status === 'deprecated' && <DropdownMenuItem onClick={() => publishMut.mutate({ id: s.id })} className="text-xs">Re-publish</DropdownMenuItem>}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => {
                        if (window.confirm(`Delete source "${s.displayName}"?`))
                          deleteMut.mutate({ id: s.id }, {
                            onSuccess: () => toast.success('Deleted'),
                            onError: (e: any) => toast.error(getApiErrorMessage(e, 'Delete failed')),
                          });
                      }} className="text-xs text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SourceEditorDialog open={showCreate} onOpenChange={setShowCreate} source={null} />
      <SourceEditorDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} source={editing} />
      {unpublishTarget && (
        <LibraryUnpublishDialog
          open={true}
          onOpenChange={(o) => !o && setUnpublishTarget(null)}
          resourceLabel={`source "${unpublishTarget.displayName}"`}
          busy={unpublishMut.isPending}
          onConfirm={async ({ reason, nextStatus }) => {
            await unpublishMut.mutateAsync({ id: unpublishTarget.id, reason, nextStatus });
            toast.success(`Unpublished — now ${nextStatus}`);
            setUnpublishTarget(null);
          }}
        />
      )}
    </div>
  );
}

function SourceEditorDialog({
  open, onOpenChange, source,
}: { open: boolean; onOpenChange: (o: boolean) => void; source: SysLibSource | null }) {
  const isEdit = source !== null;
  const [displayName, setDisplayName] = useState(source?.displayName ?? '');
  const [connectorKind, setConnectorKind] = useState<typeof CONNECTOR_KINDS[number]>(
    (source?.connectorKind as any) ?? 'webhook',
  );
  const [syncSchedule, setSyncSchedule] = useState<typeof SCHEDULES[number]>(
    (source?.syncSchedule as any) ?? 'every_15min',
  );
  const [syncEnabled, setSyncEnabled] = useState(source?.syncEnabled ?? true);
  const [submitting, setSubmitting] = useState(false);

  useMemo(() => {
    if (open) {
      setDisplayName(source?.displayName ?? '');
      setConnectorKind((source?.connectorKind as any) ?? 'webhook');
      setSyncSchedule((source?.syncSchedule as any) ?? 'every_15min');
      setSyncEnabled(source?.syncEnabled ?? true);
    }
  }, [open, source]);

  const createMut = useCreateSysLibSource();
  const updateMut = useUpdateSysLibSource(source?.id ?? '');

  const submit = async () => {
    if (!displayName.trim()) return toast.error('Display name is required');
    setSubmitting(true);
    try {
      if (isEdit && source) {
        await updateMut.mutateAsync({ displayName: displayName.trim(), syncSchedule, syncEnabled });
        toast.success('Source updated');
      } else {
        await createMut.mutateAsync({ displayName: displayName.trim(), connectorKind, syncSchedule, syncEnabled });
        toast.success('Source created (status: draft)');
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit System Source' : 'New System Source'}</DialogTitle>
          <DialogDescription>External connectors that feed system Library content.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><Label htmlFor="dn">Display name</Label><Input id="dn" value={displayName} onChange={(e) => setDisplayName(e.target.value)} /></div>
          {!isEdit && (
            <div className="space-y-2">
              <Label htmlFor="kind">Kind</Label>
              <Select value={connectorKind} onValueChange={(v) => setConnectorKind(v as any)}>
                <SelectTrigger id="kind"><SelectValue /></SelectTrigger>
                <SelectContent>{CONNECTOR_KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="sch">Sync schedule</Label>
            <Select value={syncSchedule} onValueChange={(v) => setSyncSchedule(v as any)}>
              <SelectTrigger id="sch"><SelectValue /></SelectTrigger>
              <SelectContent>{SCHEDULES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="en">Sync enabled</Label>
            <Switch id="en" checked={syncEnabled} onCheckedChange={setSyncEnabled} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !displayName.trim()}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}{isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
